import { IPageData, IPageDataV2 } from '@df/catalog/cms/cms-model.interface';
import { TLoadDependeciesParams } from '@df/catalog/model';
import { DependencyLoader } from '@df/catalog/model/dependency-loader';
import { ICmsPage } from '@icms/core/resource/page/icms-page';
import compact from 'lodash-es/compact';
import flatten from 'lodash-es/flatten';
import uniq from 'lodash-es/uniq';
import { NBlogCategoryPageType } from '../../../cms/blog/page/category-view/cms-blog-page-category-view.component';
import { NBlogPostPageType } from '../../../cms/blog/page/post-view/cms-blog-page-post-view.component';
import { NBlogRootPageType } from '../../../cms/blog/page/root-view/cms-blog-page-root-view.component';
import { LocalPageResource } from './local-page.resource';

export class LocalICmsPage<T extends IPageData = IPageData> extends ICmsPage<T> {
    /**
     * Linked posts by tag
     * blog-category type only
     */
    private tagLinkedPosts?: LocalICmsPage<IPageDataV2>[] | undefined;

    protected override addDependenciesToLoader(params: TLoadDependeciesParams): void {
        super.addDependenciesToLoader(params);
        const loader = params.loader;
        if ((this.verbosity || 0) > 2 && params.dependencyLevel >= 20) {
            switch (this.rawData?.pageType) {
                case NBlogCategoryPageType.typeId:
                    // for blog category load all posts
                    loader.add(this.loadBlogCategoryPosts(loader));
                    // for blog category load all blog categories categories
                    loader.add((this.resources.page as unknown as LocalPageResource).loadBlogCategories());
                    loader.add(this.loadBlogCategoryPosts(loader));
                    // for blog category load root page too
                    loader.add((this.resources.page as unknown as LocalPageResource).loadBlogRoot());
                    break;
                case NBlogRootPageType.typeId:
                    // for blog category load all posts
                    loader.add((this.resources.page as unknown as LocalPageResource).loadBlogPosts());
                    // for blog category load all blog categories categories
                    loader.add((this.resources.page as unknown as LocalPageResource).loadBlogCategories());
                    break;
            }

            if (this.rawData?.pageType === NBlogCategoryPageType.typeId) {
                const allTagsIds = compact(uniq(flatten(this.getBlogCategoryPosts()?.map(i => i.getBlogPostCategoryIds()))));
                if (allTagsIds) {
                    loader.add(this.resources.page.loadAllIds(allTagsIds, 2, { safe: true }));
                }
            }
        }
    }

    /**
     * Loads categories (tags) of a blog post
     * blog-post type only
     */
    private loadBlogPostCategories(): Promise<LocalICmsPage<IPageDataV2>[]> | void {
        const categoryIds = this.getBlogPostCategoryIds();
        if (categoryIds) {
            return this.resources.page.loadAllIds(categoryIds, 2, { safe: true }) as Promise<LocalICmsPage<IPageDataV2>[]>;
        }
    }
    /**
     * Loads posts linked to a blog category (including linked by tags entry)
     * blog-category type only
     */
    private loadBlogCategoryPosts(loader: DependencyLoader): Promise<unknown> | void {
        if (this.rawData?.pageType === NBlogCategoryPageType.typeId) {
            const tagLinkedPostsPromise = this.resources.page.find({ filter: { 'typeData.tags': this.id }, verbosity: 2 });
            tagLinkedPostsPromise.then(pages => (this.tagLinkedPosts = pages as LocalICmsPage<IPageDataV2>[]));
            return Promise.all([this.loadChildren(2, { loader, dependencyLevel: 20 }), tagLinkedPostsPromise].filter(i => !!i));
        }
    }
    /**
     * Retrieves categories (tags) of a blog post
     * blog-post type only
     */
    getBlogPostCategories(): LocalICmsPage<IPageDataV2>[] | undefined {
        const ids = this.getBlogPostCategoryIds();
        return ids && (this.resources.page.getAllExisting(ids) as LocalICmsPage<IPageDataV2>[]);
    }
    /**
     * Retrieves categories (tags) ids of a blog post
     * blog-post type only
     */
    getBlogPostCategoryIds(): number[] | undefined {
        if (this.data?.pageType === NBlogPostPageType.typeId && (this.verbosity || 0) > 1) {
            return uniq(flatten([(this.data as IPageDataV2).typeData?.tags]).filter(i => !!i));
        }
        return undefined;
    }
    /**
     * Retrieves categories (tags) of a blog post
     * blog-post type only
     * @returns
     */
    getBlogCategoryPosts(): LocalICmsPage<IPageDataV2>[] | undefined {
        if ((this.verbosity || 0) < 2) {
            return undefined;
        }
        switch (this.data?.pageType) {
            case NBlogCategoryPageType.typeId:
                return (uniq([...(this.children || []), ...(this.tagLinkedPosts || [])]) as LocalICmsPage<IPageDataV2>[]).filter(
                    i => i.data?.pageType === NBlogPostPageType.typeId
                );
            case NBlogRootPageType.typeId:
                return this.resources.page
                    .getAllExisting()
                    .filter(i => i.data?.pageType === NBlogPostPageType.typeId) as LocalICmsPage<IPageDataV2>[];
        }
        return undefined;
    }
}
